import {
  Component,
  EventEmitter,
  Output,
  Input,
  ChangeDetectorRef,
  DoCheck,
  OnInit,
  OnDestroy,
  ViewChildren,
  QueryList,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import { Subscription } from "rxjs";

import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import {
  DxPopupModule,
  DxTagBoxModule,
  DxTagBoxComponent,
  DxButtonModule,
  DxLoadIndicatorModule,
  DxScrollViewModule,
} from "devextreme-angular";

import { DashboardFilterService } from "../../../services/dashboard_filter_dependent_multiselection.service";
import { LanguageService } from "@app/services/language.service";
import { State } from "@app/classes/domain/entities/website/masters/state/state";
import { Zone } from "@app/classes/domain/entities/website/masters/zone/zone";
import { District } from "@app/classes/domain/entities/website/masters/district/district";
import { Block } from "@app/classes/domain/entities/website/masters/block/block";
import { BcmPostMaster } from "@app/classes/domain/entities/website/masters/sanctioned-posts/bcm-post/bcmpost";
import { PHC } from "@app/classes/domain/entities/website/masters/phc/phc";
import { AFPostMaster } from "@app/classes/domain/entities/website/masters/sanctioned-posts/af-post/afpost";
import { SC } from "@app/classes/domain/entities/website/masters/sc/sc";
import { Village } from "@app/classes/domain/entities/website/masters/village/village";
import { AshaEmployeeRecord } from "@app/classes/domain/entities/website/masters/asha/asha-employee-record/ashaemployeerecord";

@Component({
  selector: "app-hierarchy-filter",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DxPopupModule,
    DxTagBoxModule,
    DxButtonModule,
    DxLoadIndicatorModule,
    DxScrollViewModule,
  ],
  templateUrl: "./hierarchy-filter.component.html",
  styleUrl: "./hierarchy-filter.component.scss",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HierarchyFilterComponent implements OnInit, OnDestroy, DoCheck {
  @ViewChildren(DxTagBoxComponent) tagBoxes!: QueryList<DxTagBoxComponent>;

  /**
   * Optional custom title for the filter modal.
   * If omitted, defaults to "Filter Report by" / "पदानुक्रम फिल्टर".
   */
  @Input()
  title: string = "";

  /**
   * Configures persistence:
   * - 'dashboard_filter_state': Syncs with dashboard.
   * - 'custom_key_name': Persists only for this component under its own sessionStorage key.
   * - null: Disables persistence (clean in-memory on every visit).
   */
  @Input()
  set storageKey(val: string | null | undefined) {
    if (val !== undefined) {
      this.DashboardFilterService.storageKey = val;
    }
  }

  @Output()
  applied = new EventEmitter<void>();

  @Output()
  cleared = new EventEmitter<void>();

  @Output()
  closed = new EventEmitter<void>();

  onScroll: "onScroll" | "onHover" | "always" | "never" = "onScroll";

  readonly tagBoxDropDownOptions = {
    container: ".hierarchy-filter-popup",
    maxHeight: 260,
  };

  readonly filterButtonAttr = {
    class: "filter-button",
  };

  isLoading = false;
  private syncingLoadingState = false;
  private applyRequested = false;
  private langSub?: Subscription;

  constructor(
    public DashboardFilterService: DashboardFilterService,
    public langService: LanguageService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.langSub = this.langService?.lang$?.subscribe(() => {
      this.cdr.markForCheck();
      setTimeout(() => {
        this.tagBoxes?.forEach(tb => tb.instance?.repaint());
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  get popupTitle(): string {
    if (this.title) {
      return this.title;
    }
    return this.langService.currentLang === "mr"
      ? "पदानुक्रम फिल्टर"
      : "Filter Report by";
  }

  // ============================================================
  // DISPLAY HELPERS (STRONGLY TYPED BILINGUAL FOR ALL 10 LEVELS)
  // ============================================================

  getStateDisplay = (item: State): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : item.p.name_en;
  };

  getZoneDisplay = (item: Zone): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : item.p.name;
  };

  getDistrictDisplay = (item: District): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : item.p.name_en;
  };

  getBlockDisplay = (item: Block): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : item.p.name_en;
  };

  getBCMDisplay = (item: BcmPostMaster): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.full_name_mr
      ? item.p.full_name_mr
      : (item.p.full_name || "");
  };

  getPHCDisplay = (item: PHC): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : item.p.name;
  };

  getAFDisplay = (item: AFPostMaster): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : (item.p.name || "");
  };

  getSCDisplay = (item: SC): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : item.p.name;
  };

  getVillageDisplay = (item: Village): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.name_mr
      ? item.p.name_mr
      : item.p.name_en;
  };

  getAshaDisplay = (item: AshaEmployeeRecord): string => {
    if (!item?.p) return "";
    return this.langService?.currentLang === "mr" && item.p.asha_full_name_mr
      ? item.p.asha_full_name_mr
      : item.p.asha_full_name;
  };

  // ============================================================
  // LOADING UI
  // ============================================================

  ngDoCheck(): void {
    const current = this.DashboardFilterService.IsFilterLoading;
    if (current !== this.isLoading && !this.syncingLoadingState) {
      this.syncingLoadingState = true;
      Promise.resolve().then(() => {
        this.isLoading = current;
        this.syncingLoadingState = false;
        this.cdr.markForCheck();
      });
    }
  }

  // ============================================================
  // APPLY
  // ============================================================

  onApplyClick(): void {
    this.applyRequested = true;
    this.DashboardFilterService.applyFilters();
    this.DashboardFilterService.closeFilter();
    this.applied.emit();
  }

  // ============================================================
  // CLEAR
  // ============================================================

  async onClearClick(): Promise<void> {
    this.applyRequested = true;
    await this.DashboardFilterService.clearFilters();
    this.DashboardFilterService.closeFilter();
    this.cleared.emit();
  }

  // ============================================================
  // POPUP HIDE
  // ============================================================

  onHiding(): void {
    if (!this.applyRequested) {
      this.DashboardFilterService.discardUnappliedChanges();
    }
    this.applyRequested = false;
    this.DashboardFilterService.closeFilter();
    this.closed.emit();
  }

  // ============================================================
  // ACTIVE FILTER COUNT
  // ============================================================

  get activeFilterCount(): number {
    return [
      this.DashboardFilterService.state_ids,
      this.DashboardFilterService.zone_ids,
      this.DashboardFilterService.district_ids,
      this.DashboardFilterService.block_ids,
      this.DashboardFilterService.bcm_post_ids,
      this.DashboardFilterService.phc_ids,
      this.DashboardFilterService.af_ids,
      this.DashboardFilterService.sc_ids,
      this.DashboardFilterService.village_ids,
      this.DashboardFilterService.asha_ids,
    ].reduce(
      (count, selectedIds) => count + selectedIds.length,
      0,
    );
  }

  // ============================================================
  // VISIBLE FILTER COUNT
  // ============================================================

  get visibleFilterCount(): number {
    return [
      this.DashboardFilterService.IsStateVisible,
      this.DashboardFilterService.IsZoneVisible,
      this.DashboardFilterService.IsDistrictVisible,
      this.DashboardFilterService.IsBlockVisible,
      this.DashboardFilterService.IsBCMPostVisible,
      this.DashboardFilterService.IsPHCVisible,
      this.DashboardFilterService.IsAFPostVisible,
      this.DashboardFilterService.IsSCVisible,
      this.DashboardFilterService.IsVillageVisible,
      this.DashboardFilterService.IsAshaVisible,
    ].filter(Boolean).length;
  }

  // ============================================================
  // ACTIVE
  // ============================================================

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }
}
